import { PopupButtonGoThere } from '@/components/map/render/Popup/PopupButtons/PopupButtonGoThere'
import { BlockContainer } from '@/components/ui/BlockContainer'
import { Collapse, ControlledCollapse } from '@/components/ui/Collapse'
import { poiContent } from '@/components/ui/Pois/Poi/poiContent'
import { useStringParam } from '@/hooks/useParam'
import type { Poi } from '@common/types/Poi'
import { pointToLatlng } from '@lc/libgeo'
import type { Point } from 'geojson'
import { useEffect, useRef } from 'react'
import styles from './BasePoi.scss'

export type BasePoiProps = {
	poi: Poi
	button: boolean
	isPoiBlockContainer?: boolean
	single?: boolean
	buttonLabel?: string
	iconUrl?: string
	handleClick: (poi: Poi) => void
}

export function BasePoi({
	poi,
	button = false,
	isPoiBlockContainer = false,
	single = true,
}: BasePoiProps) {
	const [open, setOpen] = useStringParam('poi', '', true)
	const ref = useRef<HTMLDivElement>(null)

	const isOpen = open === poi.category + '@' + poi.id
	useEffect(() => {
		if (!single) return
		if (!isOpen) return
		if (!ref.current) return
		const reduced = window.matchMedia(
			'(prefers-reduced-motion: true)',
		).matches
		ref.current.scrollIntoView({
			behavior: reduced ? 'instant' : 'smooth',
			block: 'nearest',
		})
	}, [single, isOpen])

	if (isPoiBlockContainer) {
		return (
			<BlockContainer className={styles.poiContainer} shadow>
				<BasePoiContent poi={poi} />
			</BlockContainer>
		)
	}

	if (single) {
		return (
			<div ref={ref}>
				<ControlledCollapse
					level="secondary"
					title={poi.name}
					open={isOpen}
					setOpen={open => {
						if (open) setOpen(poi.category + '@' + poi.id)
						else setOpen('')
					}}
				>
					<BasePoiContent poi={poi} />
					{button && (
						<PopupButtonGoThere
							type="poi"
							id={poi.id}
							pos={pointToLatlng(poi.geojson as Point)}
						/>
					)}
				</ControlledCollapse>
			</div>
		)
	}

	return (
		<Collapse level="secondary" title={poi.name}>
			<BasePoiContent poi={poi} />
			{button && (
				<PopupButtonGoThere
					type="poi"
					id={poi.id}
					pos={pointToLatlng(poi.geojson as Point)}
				/>
			)}
		</Collapse>
	)
}

export function BasePoiContent({ poi }: { poi: Poi }) {
	const Component = poiContent[poi.category] ?? null
	if (!Component) {
		console.warn('Poi category not handled yet :', poi.category)
		return null
	}
	return <Component poi={poi} />
}
